import React from 'react';
import { Box, FormGroup, Typography } from '@mui/material';
import {
  AutoCompleteSelectUI,
  CheckboxUI,
  DatePickerUI,
  DebouncedInputUI,
  FormLabelUI,
  InputUI,
  SelectUI,
} from '../Interface';
import { PropertyType } from '../../config/property';
import dayjs from 'dayjs';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { TaskTypeOption } from '../../config/taskList';
import useParam from '../../context/ParamContext';

const GanttFilter = ({
  showEventReportState,
  properties,
  initial,
  setNewTaskDialogOpen,
  selectedRowState = {},
  showAddTask = false,
  createTaskList,
  handleFilterChange,
}) => {
  const { params } = useParam();
  const { selectedRowData, setSelectedRowData, setSelectedRowId } = selectedRowState;
  const { initialState } = initial;
  const { showEventReport, setShowEventReport } = showEventReportState;

  //@TODO: kivezetni egy központi helyre
  const generateField = (property) => {
    const { type, id, name, propertyOption } = property;

    switch (type) {
      case PropertyType.COMBO:
        return (
          <AutoCompleteSelectUI
            className="ganttfilter relative -top-2"
            label={<FormLabelUI text={name} />}
            onChange={(_e, newVal) => handleFilterChange(`dynamicProperty.dyn_${id}`, newVal?.value ?? null, id)}
            dataset={propertyOption?.map((option) => ({
              label: option.name,
              value: option.id,
            }))}
            selectedValue={
              initialState.filter.filterModel.items.find((item) => item.field === `dynamicProperty.dyn_${id}`)?.value
            }
          />
        );

      case PropertyType.TEXT:
        return (
          <InputUI
            variant="standard"
            className="ganttfilter relative -top-2"
            label={<FormLabelUI text={name} />}
            onChange={(e) => handleFilterChange(`dynamicProperty.dyn_${id}`, e.target.value, id, 'contains')}
          />
        );

      case PropertyType.NUMBER:
        return (
          <InputUI
            label={<FormLabelUI text={name} />}
            type="number"
            variant="standard"
            className="ganttfilter relative -top-2"
            precision={1}
            onChange={(e) => handleFilterChange(`dynamicProperty.dyn_${id}`, e.target.value, id)}
          />
        );

      case PropertyType.DECIMAL:
        return (
          <InputUI
            label={<FormLabelUI text={name} />}
            type="number"
            variant="standard"
            className="ganttfilter relative -top-2"
            precision={0.01}
            onChange={(e) => handleFilterChange(`dynamicProperty.dyn_${id}`, e.target.value, id)}
          />
        );

      case PropertyType.DATE:
        return (
          <DatePickerUI
            variant="standard"
            value={dayjs(
              initialState.filter.filterModel.items.find((item) => item.field === `dynamicProperty.dyn_${id}`)?.value ??
                null
            )}
            onChange={(date) => {
              const fixedDate = date ? dayjs(date).format('YYYY.MM.DD') : null;
              handleFilterChange(`dynamicProperty.dyn_${id}`, fixedDate, id);
            }}
          />
        );

      default:
        return null;
    }
  };

  const getConstructionSelection = () => {
    const it = initialState.filter.filterModel.items.find((i) => i.field === 'construction');
    if (typeof it?.value === 'boolean') return it.value ? 'CONSTRUCTION' : 'OTHER';
    return 'BOTH';
  };

  return (
    <FormGroup>
      <Box display="flex" alignItems="center" gap="0.5rem" flexWrap="wrap">
        <Box>
          <CheckboxUI
            label={<FormLabelUI text="Lejelentések" />}
            checked={showEventReport}
            onChange={(e) => setShowEventReport(e.target.checked)}
          />
        </Box>

        <Box>
          <CheckboxUI
            label={<FormLabelUI text="Aktív projektek" />}
            checked={initialState.filter.filterModel.items.find((item) => item.field === 'active')?.value}
            onChange={(e) => handleFilterChange('active', e.target.checked, null, 'is')}
          />
        </Box>

        {params.TASKLIST_RES_PLANNER_SHOW_TYPE_FILTER === 'Y' && (
          <Box minWidth={150}>
            <SelectUI
              label={<FormLabelUI text="Feladattípus szűrő" />}
              value={getConstructionSelection()}
              option={TaskTypeOption}
              onChange={(e) => {
                const v = e.target.value;
                if (v === 'BOTH') {
                  handleFilterChange('construction', null, null, 'is');
                } else if (v === 'CONSTRUCTION') {
                  handleFilterChange('construction', true, null, 'is');
                } else {
                  handleFilterChange('construction', false, null, 'is');
                }
              }}
            />
          </Box>
        )}

        <Box mt="-0.2rem">
          <DebouncedInputUI
            label={<FormLabelUI text="Projekt keresés" />}
            debounceMs={800}
            setQuickFilterSearchValue={(newValue) => {
              handleFilterChange('projectName', newValue, null, 'contains');
            }}
            quickFilterSearchValue={
              initialState?.filter?.filterModel?.items?.find((i) => i.field === 'projectName')?.value
            }
          />
        </Box>

        {showAddTask && (
          <Box
            onClick={(e) => {
              if (Object.keys(selectedRowData)?.length > 0) {
                createTaskList();
              } else {
                setNewTaskDialogOpen(true);
              }
            }}
          >
            <Box
              backgroundColor="#6f9ad833"
              fontWeight="bold"
              color="#014AB1"
              display="flex"
              justifyContent="center"
              gap="0.2rem"
              padding="0.5rem"
              sx={{
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: '#6f9ad880',
                },
              }}
            >
              <AddIcon className="text-xl" />
              <Typography>Feladat</Typography>
            </Box>
          </Box>
        )}
        {selectedRowData && Object.keys(selectedRowData)?.length > 0 && (
          <Box
            onClick={() => {
              setSelectedRowData({});
              setSelectedRowId(null);
            }}
          >
            <Box
              backgroundColor="#f73a4333"
              fontWeight="bold"
              color="#c40d0dff"
              display="flex"
              justifyContent="center"
              gap="0.2rem"
              padding="0.5rem"
              sx={{
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: '#ff051252',
                },
              }}
            >
              <RemoveIcon className="text-xl" />
              <Typography>Sorkijelölés</Typography>
            </Box>
          </Box>
        )}
        {properties?.map((property) => (
          <Box key={property.id} minWidth={200}>
            {generateField(property)}
          </Box>
        ))}
      </Box>
    </FormGroup>
  );
};

export default GanttFilter;
